import * as React from 'react'
import ReactDOM from 'react-dom'
import './MyCodeMirror.css';

import "codemirror/mode/javascript/javascript"
import 'codemirror/lib/codemirror.css'
import 'codemirror/addon/display/placeholder.js'
import 'codemirror/addon/hint/show-hint'
import 'codemirror/addon/hint/show-hint.css'
import CodeMirror from 'codemirror';


import Sizer, { Size } from './Sizer'
import Token from './Token'

import {precision, roundToPrecision, toPrecision, matches} from '../util'
import {inputLabelsForCodeCall} from '../model/inputLabelsForCodeCall'
import DragManager from './DragManager'

import _ from 'lodash';
import { keys } from 'src/lib/keys'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import * as actions from '../actions'

import { nodeInfoTable } from '../model/nodeInfo'
import { Cursor } from '../lib/Cursor';
import * as Model from '../types/document'
import { dragManager, network } from '../globals';


// Improvements:
//  * Automatic font-size adjustment, if desired
//  * Number scrubbing
//  * Support for marks

class MyCodeMirror extends React.Component<{
  className?: string,
  desiredWidth?: number,
  desiredHeight?: number,
  maxFontSize?: number,
  minFontSize?: number,
  children?: React.ReactNode,
  value: string,
  onValue: (value: string) => void,
  callC?: Cursor<Model.CodeCall>,
  graphC?: Cursor<Model.Graph>,
  blockC?: Cursor<Model.Block>
}> {

  state = { fontSize: 20 }
  ref = React.createRef<HTMLDivElement>()
  mirror: CodeMirror.Editor

  render() {
    const {className, desiredWidth, desiredHeight, children} = this.props
    const {fontSize} = this.state

    // remember: children are marks

    const childrenWithMirrorInjected =
      this.mirror
      ? React.Children.map(children, (child) =>
          React.cloneElement(child as React.ReactElement<any>, {mirror: this.mirror})
        )
      : undefined

    let mirrorElement = <div ref={this.ref} />

    if (desiredWidth || desiredHeight) {
      mirrorElement =
        <Sizer onSize={this.updateFontSize}>
          {mirrorElement}
        </Sizer>
    }

    return (
      <div
        className={classNames("MyCodeMirror", className)}
        style={this.fontSizeIsControlled() ? {fontSize: fontSize}: undefined}
        >
        {mirrorElement}
        {childrenWithMirrorInjected}
      </div>
    )
  }

  componentDidMount() {
    this.mirror = CodeMirror(this.ref.current!, {
      mode: 'javascript',

      // Needed for auto-expanding height to work properly.
      viewportMargin: Infinity,

      // Use tabs to indent (no smart indent).
      smartIndent: false,
      indentUnit: 2,
      tabSize: 2,
      indentWithTabs: true,

      // Disable scrolling
      scrollbarStyle: "null",

      // Style
      placeholder: '▯',
      theme: 'material',
    })

    this.mirror.on("change", this.onMirrorChange)
    this.mirror.on("mousedown", this.onMirrorMouseDown)
    this.mirror.on("blur", this.onMirrorBlur)

    this.componentDidUpdate(undefined, undefined)
    this.forceUpdate()
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    const {value} = this.props
    if (this.mirror.getValue() !== value) {
      this.mirror.setValue(value);
      this.mirror.refresh()
    } else if (!prevState || this.state !== prevState) {
      this.mirror.refresh()
    } else if (!prevProps || this.props.children !== prevProps.children) {
      this.mirror.refresh()
    }
  }

  onMirrorChange = () => {
    const {onValue, value} = this.props
    if (onValue && this.mirror.getValue() !== value) {
      // console.log("onMirrorChange", this.mirror.getValue(), value)
      onValue(this.mirror.getValue())
    }

    if (this.mirror.hasFocus()) {
      this.showAutocomplete()
    }
  }

  onMirrorMouseDown = (mirror: CodeMirror.Editor, mouseDownEvent: MouseEvent) => {
    const el = mouseDownEvent.target
    if (!el) { return }
    if (matches(el as Element, ".cm-number")) {
      mouseDownEvent.preventDefault()
      this.startNumberScrub(mouseDownEvent)
    }
  }

  onMirrorBlur = () => {
    this.mirror.getDoc().setCursor({line: 1, ch: 0})
  }


  // ===========================================================================
  // Font size autoadjust
  // ===========================================================================

  fontSizeIsControlled(): boolean {
    const { desiredWidth, desiredHeight } = this.props
    return !!(desiredWidth || desiredHeight);
  }

  updateFontSize = ({ width, height }: Size) => {
    if (!this.fontSizeIsControlled()) { return }
    const { desiredWidth, desiredHeight, maxFontSize, minFontSize } = this.props

    const relativeScaling = Math.min(
      desiredWidth ? desiredWidth / width : Infinity,
      desiredHeight ? desiredHeight / height : Infinity
    )
    let desiredFontSize = Math.floor(Math.min(this.state.fontSize * relativeScaling, 100))
    if (maxFontSize) { desiredFontSize = Math.min(desiredFontSize, maxFontSize) }
    if (minFontSize) { desiredFontSize = Math.max(desiredFontSize, minFontSize) }
    if (Math.abs(desiredFontSize - this.state.fontSize) > 1) {
      this.setState({fontSize: desiredFontSize})
    }
  }


  // ===========================================================================
  // Autocomplete
  // ===========================================================================

  showAutocomplete() {
    // TODO: types for showHint?
    (this.mirror as any).showHint({
      hint: this.hint,
      completeSingle: false
    })
  }

  shouldShowHint(token: CodeMirror.Token) {
    return network.sample(() => {
      const {callC} = this.props

      if (token.type !== "variable") {
        return false
      }
      const letters = token.string

      return callC && !callC.get().inputs.some(({inside}) => letters.indexOf(inside) !== -1)
    })
  }

  hint = (mirror: CodeMirror.Editor) => {
    // console.log("hint")
    const cursor = mirror.getDoc().getCursor()
    const token = mirror.getTokenAt(cursor)
    // console.log("tokeN", token)
    if (!this.shouldShowHint(token)) { return; }

    const from = CodeMirror.Pos(cursor.line, token.start);
    const to = CodeMirror.Pos(cursor.line, cursor.ch);
    const letters = token.string.toLowerCase();

    const completions = this.completions(letters, from, to);

    return {
      list: completions,
      from, to
    }
  }

  // Given what you have typed so far (`letters`) and a from and to
  // CodeMirror.Pos, returns a list of completions. Each completion is an
  // object with a `render(el)` function and a `hint()` function. `render(el)`
  // should append some DOM to `el`. `hint()` should replace the selection with
  // the appropriate autocompleted value.
  //
  // See: http://codemirror.net/doc/manual.html#addon_show-hint
  completions(letters: string, from: CodeMirror.Position, to: CodeMirror.Position) {
    const {callC, graphC, blockC} = this.props
    
    if (!blockC || !graphC || !callC) { return [] }

    return network.sample(() =>
      _.concat(
        // Numbered inputs
        callC && _.compact(inputLabelsForCodeCall(callC, blockC).map((input, i) => {
          if (input.type === 'numbered') {
            return {
              render: (el: Element) => {
                ReactDOM.render(<Token dumb={true}>{"in" + input.number}</Token>, el);
              },
              hint: () => actions.act(() => {
                this.mirror.getDoc().replaceRange(callC.get().inputs[i].inside, from, to, "complete")
                this.onMirrorChange()
              })
            }
          }
          return undefined
        })),
        // Named nodes
        _.compact(_.map(nodeInfoTable(blockC), (nodeInfo, nodeId): any => {
          if (nodeInfo.name) {
            const completionLetters = nodeInfo.name.toLowerCase()
            if (_.every(letters, letter => completionLetters.indexOf(letter) !== -1)) {
              return {
                render: (el: Element) => {
                  ReactDOM.render(<Token className="font-node" dumb={true}>{nodeInfo.name}</Token>, el);
                },
                hint: () => actions.act(() => {
                  const inputId = actions.ensureCallHasInputForNode(blockC, callC, nodeId)
                  this.mirror.getDoc().replaceRange(inputId, from, to, "complete")
                  this.onMirrorChange()
                })
              }
            }
          }
          return undefined
        })),
        // New input!
        [{
          displayText: "Create new input: " + letters,
          hint: () => actions.act(() => {
            const newInputNodeId = actions.addEmptyCodeCall(graphC, letters)
            actions.addInput(callC, newInputNodeId)
            this.mirror.getDoc().replaceRange(newInputNodeId, from, to, "complete")
            this.onMirrorChange()
          })
        }]
      )
    )
  }


  // ===========================================================================
  // Number Scrubbing
  // ===========================================================================

  startNumberScrub(mouseDownEvent: MouseEvent) {
    const {start, end} = this.getTokenPositionFromCursor(mouseDownEvent);
    this.mirror.focus();
    // console.log("setting selection from startNumberScrub")
    this.mirror.getDoc().setSelection(start, end);
    this.startScrubbingSelection(mouseDownEvent);
  }

  endNumberScrub(dblClickEvent: MouseEvent) {
    const position = this.mirror.coordsChar({left: dblClickEvent.clientX, top: dblClickEvent.clientY});
    // console.log("setting cursor from endNumberScrub")
    this.mirror.getDoc().setCursor(position);
  }

  getTokenPositionFromCursor(mouseDownEvent: MouseEvent) {
    const position = this.mirror.coordsChar({left: mouseDownEvent.pageX, top: mouseDownEvent.pageY});
    // console.log("getTokenPositionFromCursor", {left: mouseDownEvent.pageX, top: mouseDownEvent.pageY}, position)
    // Make sure it's really on the right character.
    if (this.mirror.cursorCoords(position).left < mouseDownEvent.clientX) {
      position.ch++;
    }
    const token = this.mirror.getTokenAt(position);

    // start and end keep track of which span of text we'll be replacing.
    let start = {line: position.line, ch: token.start};
    const end   = {line: position.line, ch: token.end};

    // Bring in negative sign if necessary.
    if (start.ch > 0) {
      const earlyStart = {line: start.line, ch: start.ch - 1};
      if (this.mirror.getDoc().getRange(earlyStart, start) === "-") {
        // TODO: Need to check that it's not subtraction (e.g. 22-30)
        start = earlyStart;
      }
    }

    return {start, end};
  }

  startScrubbingSelection(mouseDownEvent: MouseEvent) {
    // console.log("startScrubbingSelection")

    const originalValue = +this.mirror.getDoc().getSelection();
    const thePrecision = precision(this.mirror.getDoc().getSelection());

    const startX = mouseDownEvent.clientX;

    dragManager.start(mouseDownEvent, {
      cursor: "ew-resize",
      onMove: (moveEvent: MouseEvent) => {
        // console.log("onMove")
        let dx = moveEvent.clientX - startX;
        dx = dx / 10;   /* PAPERHACK 30 */
        const delta = dx * Math.pow(10, -thePrecision);
        let newValue = originalValue + delta;
        if (keys.metaKey) {
          newValue = roundToPrecision(newValue, thePrecision - 1);
        }
        const newValueString = toPrecision(newValue, thePrecision);
        // console.log("replacing selection with", JSON.stringify(newValueString), this.mirror.getDoc().getSelection())
        this.mirror.getDoc().replaceSelection(newValueString, "around");
        this.onMirrorChange()
      }
    })
  }
}

function charToLineCh (string: string, char: number) {
  const stringUpToChar = string.substr(0, char)
  const lines = stringUpToChar.split("\n")
  return {
    line: lines.length - 1,
    ch: _.last(lines)!.length
  }
}

export class Mark extends React.Component<{
  mirror?: CodeMirror.Editor,
  from: number,
  to: number,
  children: React.ReactNode,
}> {

  el = document.createElement('span')
  mark: CodeMirror.TextMarker
  isUnmounting = false

  render() {
    const {children} = this.props

    return ReactDOM.createPortal(children, this.el)
  }

  componentDidMount() {
    // console.group("Mounting mark", this, this.el.innerText)
    this.attachMark()

    // this.mark.on('clear', () => {
    //   console.log("cleared mark", this.props, this.isUnmounting, this.el.innerText)
    // })
    // console.groupEnd()
  }

  componentDidUpdate() {
    // console.log("mark", this.props, this.el.innerText, "is at", this.mark.find())
    if (!this.mark.find()) {
      this.attachMark()
    }
  }

  attachMark() {
    const {mirror, from, to} = this.props
    const value = mirror!.getValue()

    if (this.mark) {
      this.mark.clear()
    }
    this.mark = mirror!.getDoc().markText(
      charToLineCh(value, from),
      charToLineCh(value, to),
      {
        replacedWith: this.el,
        handleMouseEvents: true
      }
    )
  }

  componentWillUnmount() {
    this.isUnmounting = true
    // console.group("Unmounting mark", this.props, this.el.innerText)
    this.mark.clear()
    // console.log("Done!")
    // console.groupEnd()
  }
}

export default MyCodeMirror;



// WEBPACK FOOTER //
// ./src/view/MyCodeMirror.tsx